import {
    Alert,
    Loading,
    StyleFunction,
    Surface,
    Tabs,
    useAuth,
    useForm,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    useLayoutLazyQuery,
    useLayoutSaveMutation,
    useProductLayoutFormLazyQuery,
    useVenueAccessTicketParametersBulkQuery,
} from "graphql-sdk";
import {
    FunctionButton,
    ILayoutInput,
    schemaEditLayout,
    VenueAccessTicketButtonType,
} from "lib";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "locales";

import { useHandleMutationError } from "../../../hooks/use-handle-mutation-error";
import { RootStackScreenProps } from "../../../navigation";
import { rinseLayoutData } from "../functions";
import { LayoutForm } from "../layout-form";
import { LayoutScreen } from "../layout-screen";
import { LayoutEditor } from "./layout-editor";
import { useFocusEffect } from "@react-navigation/native";

type ScreenProps = RootStackScreenProps<"LAYOUT_EDIT">;

export function LayoutEditScreen({
    navigation: { navigate },
    route,
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.layout.write");

    const focusedOnce = useRef<boolean>(false);
    const lastAppliedLayoutId = useRef<string | null>(null);

    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const { handleMutationError } = useHandleMutationError();
    const [layoutEdit] = useLayoutSaveMutation();
    const layoutId = route.params.id;

    useEffect(() => {
        if (!layoutId) {
            return;
        }

        lastAppliedLayoutId.current = null;
    }, [layoutId]);

    const [getLayout, layoutQuery] = useLayoutLazyQuery({
        fetchPolicy: "cache-and-network",
    });
    const form = useForm<ILayoutInput>(schemaEditLayout, null);
    const [{ values }, { setDefaultValues }] = form;

    const venueAccessEventIds = useMemo(() => {
        if (!values?.data) {
            return [];
        }
        const ids = new Set<number>();
        for (const section of values.data) {
            for (const button of section.buttons) {
                if (
                    button &&
                    button.buttonType === "FUNCTION" &&
                    (button as FunctionButton).function ===
                        "VENUE_ACCESS_TICKETS" &&
                    (button as VenueAccessTicketButtonType).venueAccessEvent !=
                        null
                ) {
                    ids.add(
                        (button as VenueAccessTicketButtonType).venueAccessEvent
                    );
                }
            }
        }
        return Array.from(ids);
    }, [values?.data]);

    const { data: venueAccessBulkData } =
        useVenueAccessTicketParametersBulkQuery({
            variables: { eventIds: venueAccessEventIds },
            skip: venueAccessEventIds.length === 0,
        });

    const [
        getLayoutEditorData,
        { data: layoutEditorData, refetch, error, loading },
    ] = useProductLayoutFormLazyQuery({
        fetchPolicy: "cache-and-network",
    });

    useFocusEffect(
        useCallback(() => {
            if (!layoutId) {
                navigate("LAYOUTS");
                return;
            }

            if (focusedOnce.current) {
                refetch();
            } else {
                if (lastAppliedLayoutId.current !== layoutId) {
                    getLayout({ variables: { id: layoutId } });
                    getLayoutEditorData();
                }
                focusedOnce.current = true;
            }
        }, [getLayout, getLayoutEditorData, layoutId, navigate, refetch])
    );

    useEffect(() => {
        if (layoutId && layoutId === lastAppliedLayoutId.current) {
            return;
        }

        lastAppliedLayoutId.current = layoutId;
    });

    useEffect(() => {
        if (!layoutQuery.data?.layout) {
            return;
        }

        const layoutData = layoutQuery.data?.layout;

        setDefaultValues({
            name: layoutData.name,
            data: JSON.parse(layoutData.data),
        });
    }, [layoutQuery.data, setDefaultValues, values]);

    const redirect = useCallback(() => {
        // If the page was loaded with a referrer, then return to that referrer.
        // The referrer could be e.g. CASH_REGISTER_EDIT with a specific id, but could also be to the general MERCHANT_SETTINGS,
        // so the id could be missing from the referrer argument
        if (!route.params?.referrer?.route) {
            return;
        }

        if (route.params.referrer.route === "MERCHANT_SETTINGS") {
            navigate("MERCHANT_SETTINGS");
        }
    }, [route.params, navigate]);

    const handleSubmit = useCallback(async () => {
        if (!values) {
            return;
        }

        // remove metaData object from buttons. There is no need to save them.
        const updatedNewLayoutData = values.data.map(rinseLayoutData);

        await handleMutationError(
            async () => {
                await layoutEdit({
                    variables: {
                        id: layoutId,
                        layout: {
                            name: values.name,
                            data: JSON.stringify(updatedNewLayoutData),
                        },
                    },
                });
            },
            t("common.saved", "Saved"),
            // Redirect after saving, if there was a referrer.
            redirect
        );
    }, [handleMutationError, layoutEdit, layoutId, redirect, t, values]);

    const referenceData = useMemo(() => {
        if (!layoutEditorData) {
            return null;
        }

        // If there are venue access buttons, wait for bulk data before rendering
        if (venueAccessEventIds.length > 0 && !venueAccessBulkData) {
            return null;
        }

        return {
            products: layoutEditorData.products.data.map(p => ({
                ...p,
                buttonText: p.buttonText || null,
            })),
            productGroups:
                layoutEditorData.productGroups.data.map(pg => ({
                    id: pg.id,
                    color: pg.color,
                })) || [],
            events: layoutEditorData.cmpEvents.data.map(item => ({
                id: item.id,
                name: item.name,
                active: item.active,
                externalId: item.externalId,
                products: item.products,
            })),
            cmpProducts: layoutEditorData.cmpProducts.data.map(item => ({
                id: item.id,
                name: item.name,
                active: item.active,
                externalId: item.externalId,
                amount: item.amount,
            })),
            layoutEntities: layoutEditorData.layoutEntities.data.map(item => ({
                id: item.id,
                name: item.name,
            })),
            venueAccessEvents:
                venueAccessBulkData?.venueAccessTicketParametersBulk.events ??
                [],
            venueAccessAccessTypes:
                venueAccessBulkData?.venueAccessTicketParametersBulk
                    .accessTypes ?? [],
        };
    }, [layoutEditorData, venueAccessBulkData, venueAccessEventIds]);

    return (
        <LayoutScreen>
            <Surface style={styles.container}>
                {layoutQuery.error ? (
                    <Alert type="error">
                        {t(
                            "backoffice.error.from_server",
                            "There was an error: {{errorText}}",
                            {
                                errorText: layoutQuery.error.message,
                            }
                        )}
                    </Alert>
                ) : null}
                {error ? (
                    <Alert type="error">
                        {t(
                            "backoffice.error.from_server",
                            "There was an error: {{errorText}}",
                            {
                                errorText: error.message,
                            }
                        )}
                    </Alert>
                ) : null}
                {layoutQuery.loading || loading ? (
                    <Loading />
                ) : layoutQuery.data && referenceData ? (
                    <Tabs
                        labels={[
                            t("backoffice.layouts.layout", "Layout"),
                            t("backoffice.details", "Details"),
                        ]}
                    >
                        <LayoutEditor
                            form={form}
                            referenceData={referenceData}
                            onSubmit={handleSubmit}
                            dimensions={{
                                columns: layoutQuery.data.layout.columns,
                                rows: layoutQuery.data.layout.rows,
                            }}
                        />
                        <LayoutForm
                            form={form}
                            onSubmit={handleSubmit}
                            submitButton={["common.save", "Save"]}
                        />
                    </Tabs>
                ) : null}
            </Surface>
        </LayoutScreen>
    );
}

const styleFunc: StyleFunction = (theme, dimensions) => ({
    container: {
        maxWidth: Math.min(dimensions.width, 1350),
    },
});
